import {
    APPLICATION_JSON,
    DEFAULT_MIX,
    DEFAULT_PERSONAL_MIX,
} from '../constants';
import {fetchWithInfoLog} from '../fetcher';
import {logger} from '../logger';
import type {
    APIType,
    ClientProps,
    GenerateEndpoint,
    GetEndpointParams,
    Participant,
    RoomID,
} from '../types';

/**
 * Creates conference-level action handlers.
 *
 * These actions map to the Pexip REST API conference control functions
 * (`/api/client/v2/conferences/<conference_alias>/<request>`).
 * Host-only actions are noted in individual docs.
 *
 * All actions automatically resolve `conferenceAlias`, `host`, and
 * `breakoutUuid` from `clientProps` when not explicitly provided.
 *
 * @see {@link https://docs.pexip.com/api_client/api_rest.htm | Pexip client REST API}
 */
export const createConferenceActions = ({
    API,
    clientProps,
    generateEndpoint,
    getMe,
}: {
    API: APIType;
    clientProps: ClientProps;
    generateEndpoint: GenerateEndpoint;
    getMe: (roomId?: RoomID) => Participant | undefined;
}) => {
    type Defaults = Pick<
        ClientProps,
        'conferenceAlias' | 'host' | 'breakoutUuid'
    >;
    const withDefaults = <T>(p: T): Defaults & T =>
        ({
            conferenceAlias: clientProps.conferenceAlias,
            host: clientProps.host,
            breakoutUuid: clientProps.breakoutUuid,
            ...p,
        }) as Defaults & T;

    /**
     * Returns the list of available layouts for the conference.
     *
     * Includes built-in layouts plus any custom layouts from the theme.
     * Pass `annotate: true` for richer metadata (tags).
     *
     * REST: `GET /conferences/<alias>/available_layouts`
     */
    const availableLayouts = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'availableLayouts'>,
            fetcher,
        ) => {
            const {
                conferenceAlias,
                host,
                annotate = false,
            } = withDefaults(params);

            const res = await API.availableLayouts({
                fetcher,
                params: {
                    conferenceAlias,
                    annotate,
                },
                host,
            });

            return res.data.result;
        },
        funcName: 'availableLayouts',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Removes a personal video mix, reverting the participant to the conference layout.
     *
     * Supports breakout rooms.
     *
     * REST: `POST /conferences/<alias>/video_mixes/<name>/delete`
     */
    const deleteVideoMix = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'deleteVideoMix'>,
            fetcher,
        ) => {
            const {
                conferenceAlias,
                host,
                breakoutUuid,
                mixName = getMe()?.receiveFromVideoMix?.mix_name,
            } = withDefaults(params);

            if (!mixName) {
                throw new Error('Mix name is required');
            }

            return API.videoMixesDelete({
                fetcher,
                params: {conferenceAlias, breakoutUuid, mixName},
                host,
            });
        },
        funcName: 'deleteVideoMix',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Dials out from the conference to a target endpoint.
     *
     * Host-only. Returns the UUID(s) of newly created participant(s).
     * The dialed participant initially appears with `service_type: "connecting"`
     * and transitions to `"conference"` when answered.
     *
     * REST: `POST /conferences/<alias>/dial`
     */
    const dial = generateEndpoint({
        func: async ([params]: GetEndpointParams<'dial'>, fetcher) => {
            const {
                conferenceAlias,
                host,
                protocol = 'auto',
                ...body
            } = withDefaults(params);

            return API.dial({
                fetcher,
                params: {
                    conferenceAlias,
                },
                body: {
                    protocol,
                    ...body,
                },
                host,
            });
        },
        funcName: 'dial',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Disconnects all conference participants, including the caller.
     *
     * Host-only. Supports breakout rooms.
     *
     * REST: `POST /conferences/<alias>/disconnect`
     */
    const disconnectAll = generateEndpoint({
        func: async ([params]: GetEndpointParams<'disconnectAll'>, fetcher) => {
            const {conferenceAlias, breakoutUuid, host} = withDefaults(params);

            return API.disconnect({
                fetcher,
                params: {conferenceAlias, breakoutUuid},
                host,
            });
        },
        funcName: 'disconnectAll',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Configures whether Guests can unmute themselves after being muted by a Host.
     *
     * Host-only. Supports breakout rooms.
     *
     * REST: `POST /conferences/<alias>/set_guests_can_unmute`
     */
    const guestsCanUnmute = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'guestsCanUnmute'>,
            fetcher,
        ) => {
            const {setting, conferenceAlias, breakoutUuid, host} =
                withDefaults(params);

            const body = {setting};

            return API.setGuestsCanUnmute({
                fetcher,
                params: {conferenceAlias, breakoutUuid},
                host,
                body,
            });
        },
        funcName: 'guestsCanUnmute',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Returns SVG string representations of the active conference layouts.
     *
     * REST: `GET /conferences/<alias>/layout_svgs`
     */
    const layoutSvgs = generateEndpoint({
        func: async ([params]: GetEndpointParams<'layoutSvgs'>, fetcher) => {
            const {conferenceAlias, host} = withDefaults(params);

            const res = await API.layoutSvgs({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });

            return res.data.result;
        },
        funcName: 'layoutSvgs',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Locks or unlocks the conference.
     *
     * When locked, new participants are held in a "Waiting for Host" screen
     * until admitted via {@link participantsActions.admit}.
     * Host-only. Supports breakout rooms.
     *
     * REST: `POST /conferences/<alias>/lock` or `unlock`
     */
    const lock = generateEndpoint({
        func: async ([params]: GetEndpointParams<'lock'>, fetcher) => {
            const {lock, conferenceAlias, host, breakoutUuid} =
                withDefaults(params);

            const apiFn = lock ? API.lock : API.unlock;

            return apiFn({
                fetcher,
                params: {conferenceAlias, breakoutUuid},
                host,
            });
        },
        funcName: 'lock',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Lowers all raised hands in the conference.
     *
     * Supports breakout rooms.
     *
     * REST: `POST /conferences/<alias>/clearallbuzz`
     */
    const lowerAllRaisedHands = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'lowerAllRaisedHands'>,
            fetcher,
        ) => {
            const {conferenceAlias, host, breakoutUuid} = withDefaults(params);

            return API.clearAllBuzz({
                fetcher,
                params: {conferenceAlias, breakoutUuid},
                host,
            });
        },
        funcName: 'lowerAllRaisedHands',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Mutes or unmutes all Guest participants in the conference.
     *
     * When muted, no Guest can speak unless individually unmuted.
     * Host-only. Supports breakout rooms.
     *
     * REST: `POST /conferences/<alias>/muteguests` or `unmuteguests`
     */
    const muteAllGuests = generateEndpoint({
        func: async ([params]: GetEndpointParams<'muteAllGuests'>, fetcher) => {
            const {mute, conferenceAlias, breakoutUuid, host} =
                withDefaults(params);

            const apiFn = mute ? API.muteguests : API.unmuteguests;

            return apiFn({
                fetcher,
                params: {conferenceAlias, breakoutUuid},
                host,
            });
        },
        funcName: 'muteAllGuests',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Returns the full participant list of the conference.
     *
     * REST: `GET /conferences/<alias>/participants`
     */
    const requestParticipants = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'requestParticipants'>,
            fetcher,
        ) => {
            const {conferenceAlias, host} = withDefaults(params);

            try {
                return await API.participants({
                    fetcher,
                    params: {
                        conferenceAlias,
                    },
                    host,
                });
            } catch (error) {
                logger.error({error}, `Can't request participants.`);
            }
        },
        funcName: 'requestParticipants',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Fetches the theme resources for the conference.
     *
     * Used with direct media to locally render splash screens.
     * Returns the parsed theme schema, or `undefined` on failure.
     *
     * REST: `GET /conferences/<alias>/theme`
     */
    const requestTheme = generateEndpoint({
        func: async ([params]: GetEndpointParams<'requestTheme'>, fetcher) => {
            const {conferenceAlias, host} = withDefaults(params);

            try {
                const response = await API.theme({
                    fetcher,
                    params: {
                        conferenceAlias,
                    },
                    host,
                });

                if (response.status !== 200) {
                    return;
                }

                return response.data.result;
            } catch (error) {
                logger.error({error}, `Can't request theme.`);
            }
        },
        funcName: 'requestTheme',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Sends a generic conference-scoped REST request.
     *
     * Allows calling arbitrary conference API paths not covered by
     * dedicated action methods. The response is returned as raw JSON.
     */
    const sendConferenceRequest = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'sendConferenceRequest'>,
            fetcher,
        ) => {
            const {path, method, payload} = params;
            const uri = `${clientProps.host}/api/client/v2/conferences/${clientProps.conferenceAlias}/${path}`;
            try {
                const body = payload ? JSON.stringify(payload) : undefined;
                const res = await fetcher(uri, {
                    method,
                    body,
                    headers: {
                        'Content-Type': APPLICATION_JSON,
                    },
                });

                const responseContentType = res.headers.get('Content-Type');
                if (
                    !responseContentType
                        ?.toLowerCase()
                        .startsWith(APPLICATION_JSON)
                ) {
                    logger.error(
                        {responseContentType, APPLICATION_JSON},
                        `Response Content-Type is not ${APPLICATION_JSON}`,
                    );
                    return {status: res.status, data: undefined};
                }
                try {
                    const data: unknown = await res.json();
                    return {status: res.status, data};
                } catch (error) {
                    logger.error({error}, 'Could not get response data: ');
                }
            } catch (error) {
                logger.error({error}, 'Could not send conference request');
            }
        },
        funcName: 'sendConferenceRequest',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Changes the conference layout, streaming content, indicators, and overlay text.
     *
     * Multiple requests are applied cumulatively (most recent settings take precedence).
     * Use `{transforms: {}}` to reset all parameters.
     * Supports breakout rooms.
     *
     * REST: `POST /conferences/<alias>/transform_layout`
     */
    const setLayout = generateEndpoint({
        func: async ([params]: GetEndpointParams<'setLayout'>, fetcher) => {
            const {transforms, conferenceAlias, host, breakoutUuid} =
                withDefaults(params);

            const body = {transforms};

            return API.transformLayout({
                fetcher,
                params: {conferenceAlias, breakoutUuid},
                host,
                body,
            });
        },
        funcName: 'setLayout',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Applies a personal layout to the current participant via video mixes.
     *
     * Creates a personal video mix (if the participant is on the default mix)
     * then configures it with the requested layout transforms.
     * Supports breakout rooms.
     *
     * REST: `POST /conferences/<alias>/video_mixes/create` +
     *       `POST /conferences/<alias>/video_mixes/<name>/configure`
     */
    const setPersonalLayout = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'setPersonalLayout'>,
            fetcher,
        ) => {
            const {
                transforms,
                conferenceAlias,
                host,
                breakoutUuid,
                mixName = DEFAULT_PERSONAL_MIX,
            } = withDefaults(params);

            const currentMixName = getMe()?.receiveFromVideoMix?.mix_name;

            if (currentMixName === DEFAULT_MIX) {
                await API.videoMixesCreate({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                    body: {
                        mix_name: mixName,
                    },
                });
            }

            return API.videoMixesConfigure({
                fetcher,
                params: {
                    conferenceAlias,
                    breakoutUuid,
                    mixName,
                },
                host,
                body: {
                    transform_layout: transforms,
                },
            });
        },
        funcName: 'setPersonalLayout',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Changes a participant's name overlay text.
     *
     * The text is only applied if overlay text is enabled on the VMR.
     *
     * REST: `POST /conferences/<alias>/participants/<uuid>/overlaytext`
     */
    const setTextOverlay = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'setTextOverlay'>,
            fetcher,
        ) => {
            const {text, conferenceAlias, host, participantUuid} =
                withDefaults(params);

            return API.overlaytextParticipant({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid,
                },
                body: {
                    text,
                },
                host,
            });
        },
        funcName: 'setTextOverlay',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Starts the conference, allowing Guests in the waiting room to join.
     *
     * Required when the only Host is connected without media
     * (presentation and control-only). Host-only.
     *
     * REST: `POST /conferences/<alias>/start_conference`
     */
    const startConference = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'startConference'>,
            fetcher,
        ) => {
            const {conferenceAlias, host} = withDefaults(params);

            return API.startConference({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'startConference',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Retrieves the available languages for live captions.
     *
     * REST: `GET /participants/<uuid>/live_captions_languages`
     */
    const liveCaptionsLanguages = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'liveCaptionsLanguages'>,
            fetcher,
        ) => {
            const {conferenceAlias, breakoutUuid} = withDefaults(params);

            return API.liveCaptionsLanguages({
                fetcher,
                params: {
                    conferenceAlias,
                    breakoutUuid,
                },
                host: clientProps.host,
            });
        },
        funcName: 'liveCaptionsLanguages',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    return {
        availableLayouts,
        deleteVideoMix,
        dial,
        disconnectAll,
        guestsCanUnmute,
        layoutSvgs,
        liveCaptionsLanguages,
        lock,
        lowerAllRaisedHands,
        muteAllGuests,
        requestParticipants,
        requestTheme,
        sendConferenceRequest,
        setLayout,
        setPersonalLayout,
        setTextOverlay,
        startConference,
    };
};
